import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Image, Plus, ArrowLeft, Video, Shirt, Camera, Music, Trash2,
  ExternalLink, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  film: Video,
  fashion: Shirt,
  photography: Camera,
  content: Camera,
  music: Music
};

const categoryColors = {
  film: 'bg-violet-500/20 text-violet-300 border-violet-400',
  fashion: 'bg-pink-500/20 text-pink-300 border-pink-400',
  photography: 'bg-cyan-500/20 text-cyan-300 border-cyan-400',
  content: 'bg-blue-500/20 text-blue-300 border-blue-400',
  music: 'bg-amber-500/20 text-amber-300 border-amber-400'
};

export default function PortfolioGallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newItem, setNewItem] = useState({
    title: '',
    category: 'photography',
    description: '',
    media_url: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPortfolio();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchPortfolio = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/studio/portfolio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching portfolio:', err);
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.title || !newItem.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/studio/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newItem)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Portfolio item added!');
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setShowAddModal(false);
        setNewItem({ title: '', category: 'photography', description: '', media_url: '', tags: [] });
        fetchPortfolio();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add item');
      }
    } catch (err) {
      toast.error('Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newItem.tags.includes(tagInput.trim())) {
      setNewItem({ ...newItem, tags: [...newItem.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewItem({ ...newItem, tags: newItem.tags.filter(t => t !== tag) });
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.category === filter);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="portfolio-gallery">
        <div className="container mx-auto px-4 py-20 text-center">
          <Image className="h-16 w-16 text-violet-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Build Your Portfolio</h1>
          <p className="text-violet-200 mb-8 max-w-md mx-auto">
            Showcase your creative work, attract clients, and land opportunities. 
            Log in to start building your portfolio.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-violet-600 hover:bg-violet-700">
              Log In to Get Started
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="portfolio-gallery">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-12">
        <div className="container mx-auto px-4">
          <Link to="/legacystudio" className="inline-flex items-center text-violet-300 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyStudio
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Image className="h-8 w-8 text-violet-400" />
                <h1 className="text-3xl font-bold text-white">My Portfolio</h1>
              </div>
              <p className="text-violet-200">Showcase your creative work and build your brand.</p>
            </div>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="h-4 w-4 mr-2" /> Add Work
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-900 border-violet-500/30 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Portfolio Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={addItem} className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-violet-300 mb-1 block">Title *</label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      placeholder="Project title"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-violet-300 mb-1 block">Category *</label>
                    <Select 
                      value={newItem.category} 
                      onValueChange={(val) => setNewItem({ ...newItem, category: val })}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-800 border-white/10">
                        <SelectItem value="photography">Photography</SelectItem>
                        <SelectItem value="film">Film/Video</SelectItem>
                        <SelectItem value="fashion">Fashion</SelectItem>
                        <SelectItem value="content">Content Creation</SelectItem>
                        <SelectItem value="music">Music</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-violet-300 mb-1 block">Description *</label>
                    <Textarea
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      placeholder="Describe your work..."
                      className="bg-white/5 border-white/10 text-white"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-violet-300 mb-1 block">Media URL (optional)</label>
                    <Input
                      value={newItem.media_url}
                      onChange={(e) => setNewItem({ ...newItem, media_url: e.target.value })}
                      placeholder="https://..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-violet-300 mb-1 block">Tags</label>
                    <div className="flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        placeholder="Add tag and press Enter"
                        className="bg-white/5 border-white/10 text-white flex-1"
                      />
                      <Button type="button" onClick={addTag} variant="outline" className="border-violet-400 text-violet-300">
                        Add
                      </Button>
                    </div>
                    {newItem.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {newItem.tags.map(tag => (
                          <Badge 
                            key={tag} 
                            className="bg-violet-500/20 text-violet-200 cursor-pointer hover:bg-red-500/20"
                            onClick={() => removeTag(tag)}
                          >
                            {tag} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-violet-600 hover:bg-violet-700">
                      {submitting ? 'Adding...' : 'Add to Portfolio'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {['all', 'photography', 'film', 'fashion', 'content', 'music'].map(cat => (
            <Button
              key={cat}
              variant={filter === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(cat)}
              className={filter === cat 
                ? 'bg-violet-600 hover:bg-violet-700' 
                : 'border-violet-400/50 text-violet-200 hover:bg-violet-500/20'
              }
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </Button>
          ))}
        </div>

        {/* Portfolio Grid */}
        {filteredItems.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-16 text-center">
              <Image className="h-16 w-16 text-violet-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {filter === 'all' ? 'Your portfolio is empty' : `No ${filter} items yet`}
              </h3>
              <p className="text-violet-300 mb-6">Start adding your creative work to build your portfolio.</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-violet-600 hover:bg-violet-700">
                <Plus className="h-4 w-4 mr-2" /> Add Your First Item
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, idx) => {
              const Icon = categoryIcons[item.category] || Image;
              const colorClass = categoryColors[item.category] || categoryColors.content;

              return (
                <motion.div
                  key={item.item_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all group overflow-hidden">
                    {/* Media Preview */}
                    <div className="aspect-video bg-gradient-to-br from-violet-900/50 to-purple-900/50 flex items-center justify-center relative overflow-hidden">
                      {item.media_url ? (
                        <img 
                          src={item.media_url} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <Icon className="h-16 w-16 text-violet-400/50" />
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        {item.media_url && (
                          <a href={item.media_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="secondary">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>

                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className={categoryColors[item.category]}>
                          {item.category}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-white text-lg mb-2">{item.title}</h3>
                      <p className="text-violet-200 text-sm line-clamp-2 mb-3">{item.description}</p>
                      
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tags.slice(0, 3).map(tag => (
                            <Badge key={tag} variant="outline" className="border-violet-400/30 text-violet-300 text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {item.tags.length > 3 && (
                            <Badge variant="outline" className="border-violet-400/30 text-violet-300 text-xs">
                              +{item.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
